import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const EMPTY_FORM = {
  id: null,
  concession_id: '',
  nom: '',
  adresse: '',
  code_postal: '',
  ville: '',
  departement: '',
  siret: '',
};

export default function EntitesJuridiques({ user, concessions = [] }) {
  const [entites, setEntites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterConcession, setFilterConcession] = useState('');

  const isSuperAdmin = user?.role === 'super-admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/entites-juridiques');
      setEntites(res.data.entites || []);
    } catch (err) {
      console.error('Erreur chargement entités juridiques:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (e) => {
    setForm({
      id: e.id,
      concession_id: e.concession_id || '',
      nom: e.nom || '',
      adresse: e.adresse || '',
      code_postal: e.code_postal || '',
      ville: e.ville || '',
      departement: e.departement || '',
      siret: e.siret || '',
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.nom.trim() || !form.concession_id) {
      alert('Nom et concession obligatoires');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        concession_id: form.concession_id ? parseInt(form.concession_id, 10) : null,
        nom: form.nom.trim(),
        adresse: form.adresse.trim() || null,
        code_postal: form.code_postal.trim() || null,
        ville: form.ville.trim() || null,
        departement: form.departement.trim() || null,
        siret: form.siret.trim() || null,
      };
      if (form.id) {
        await api.put(`/entites-juridiques/${form.id}`, payload);
      } else {
        await api.post('/entites-juridiques', payload);
      }
      setShowModal(false);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Supprimer l'entité juridique "${e.nom}" ?`)) return;
    try {
      await api.delete(`/entites-juridiques/${e.id}`);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = entites.filter(e => {
    if (filterConcession && String(e.concession_id) !== String(filterConcession)) return false;
    if (!q) return true;
    return (
      (e.nom || '').toLowerCase().includes(q) ||
      (e.siret || '').toLowerCase().includes(q) ||
      (e.ville || '').toLowerCase().includes(q)
    );
  });

  // Concessions présentes dans les entités, pour le filtre
  const concessionsDansEntites = Array.from(
    new Map(entites.map(e => [e.concession_id, { id: e.concession_id, nom: e.concession_nom }])).values()
  ).filter(c => c.id).sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));

  return (
    <div>
      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
        {isSuperAdmin && (
          <button className="btn-primary" onClick={openCreate}>➕ Nouvelle entité juridique</button>
        )}
        <input
          className="page-search"
          placeholder="🔍 Rechercher..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {concessionsDansEntites.length > 1 && (
          <select
            className={`page-select${filterConcession ? ' active-filter' : ''}`}
            value={filterConcession}
            onChange={(e) => setFilterConcession(e.target.value)}
          >
            <option value="">Toutes concessions</option>
            {concessionsDansEntites.map(c => (
              <option key={c.id} value={c.id}>{c.nom}</option>
            ))}
          </select>
        )}
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Nom</th>
              <th>Concession</th>
              <th>Adresse</th>
              <th>CP / Ville</th>
              <th>Département</th>
              <th>SIRET</th>
              {isSuperAdmin && <th style={{ width: 120 }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={isSuperAdmin ? 7 : 6} style={{ textAlign: 'center', padding: 30, color: 'var(--text-secondary)' }}>Chargement...</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={isSuperAdmin ? 7 : 6} style={{ textAlign: 'center', padding: 30, color: 'var(--text-secondary)' }}>
                Aucune entité juridique. {isSuperAdmin && 'Cliquez sur "Nouvelle entité juridique" pour en créer une.'}
              </td></tr>
            )}
            {filtered.map(e => (
              <tr key={e.id}>
                <td style={{ fontWeight: 600 }}>{e.nom}</td>
                <td>{e.concession_nom || '—'}</td>
                <td>{e.adresse || '—'}</td>
                <td>{[e.code_postal, e.ville].filter(Boolean).join(' ') || '—'}</td>
                <td>{e.departement || '—'}</td>
                <td style={{ fontFamily: 'monospace' }}>{e.siret || '—'}</td>
                {isSuperAdmin && (
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon" title="Modifier" onClick={() => openEdit(e)}>✏️</button>
                      <button className="btn-icon delete" title="Supprimer" onClick={() => remove(e)}>🗑️</button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{form.id ? 'Modifier l\'entité juridique' : 'Nouvelle entité juridique'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Concession *</label>
                  <select
                    value={form.concession_id}
                    onChange={(e) => setForm({ ...form, concession_id: e.target.value })}
                    style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                  >
                    <option value="">— Choisir une concession —</option>
                    {concessions.map(c => (
                      <option key={c.id} value={c.id}>{c.nom}</option>
                    ))}
                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Nom *</label>
                  <input
                    type="text"
                    value={form.nom}
                    onChange={(e) => setForm({ ...form, nom: e.target.value })}
                    placeholder="SOGUAVA, SOREC..."
                    style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Adresse</label>
                  <input
                    type="text"
                    value={form.adresse}
                    onChange={(e) => setForm({ ...form, adresse: e.target.value })}
                    style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Code postal</label>
                  <input
                    type="text"
                    value={form.code_postal}
                    onChange={(e) => setForm({ ...form, code_postal: e.target.value })}
                    style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Ville</label>
                  <input
                    type="text"
                    value={form.ville}
                    onChange={(e) => setForm({ ...form, ville: e.target.value })}
                    style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Département</label>
                  <input
                    type="text"
                    value={form.departement}
                    onChange={(e) => setForm({ ...form, departement: e.target.value })}
                    placeholder="Guadeloupe, 971..."
                    style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>SIRET</label>
                  <input
                    type="text"
                    value={form.siret}
                    onChange={(e) => setForm({ ...form, siret: e.target.value })}
                    placeholder="14 chiffres"
                    style={{ width: '100%', padding: '8px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontFamily: 'monospace' }}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>Annuler</button>
              <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Enregistrement...' : 'Enregistrer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
